import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface ChangeErrorDisplayProps {
  errors: string[];
  isEmptyReport?: boolean;
  changeString: string;
  onChangeStringUpdate: (value: string) => void;
}

const Container = styled.div`
  padding: 16px;
`;

const ErrorsSection = styled.div`
  margin-bottom: 20px;
`;

const ErrorsList = styled.div`
  margin-bottom: 16px;
`;

const ErrorsTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #d32f2f;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const ErrorItem = styled.li`
  margin-bottom: 4px;
  color: #d32f2f;
`;

const ExplanationSection = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 500;
`;

const TextArea = styled.textarea`
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  background-color: #fff;
  color: #333;
  padding-bottom: 24px;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
`;

const CharacterCount = styled.div`
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 12px;
  color: var(--sumi-text-secondary);
  pointer-events: none;
`;

const ChangeErrorDisplay: React.FC<ChangeErrorDisplayProps> = ({
  errors,
  isEmptyReport = false,
  changeString,
  onChangeStringUpdate,
}) => {
  const { t } = useTranslation();
  const hasErrors = errors.length > 0;
  const requiresComment = hasErrors || isEmptyReport;
  const maxLength = 6000;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      onChangeStringUpdate(value);
    }
  };

  return (
    <Container>
      {requiresComment && (
        <ErrorsSection>
          {hasErrors && (
            <ErrorsList>
              <ErrorsTitle>{t("header-changes-detected")}</ErrorsTitle>
              <ErrorList>
                {errors.map((error, index) => (
                  <ErrorItem key={index}>{error}</ErrorItem>
                ))}
              </ErrorList>
            </ErrorsList>
          )}

          {isEmptyReport && (
            <ErrorsList>
              <ErrorsTitle>{t("header-empty-report-detected")}</ErrorsTitle>
            </ErrorsList>
          )}

          <ExplanationSection>
            <Label htmlFor="change-explanation">
              {isEmptyReport && !hasErrors
                ? t("label-empty-report-explanation")
                : t("label-change-explanation")}
            </Label>
            <TextArea
              id="change-explanation"
              value={changeString}
              onChange={handleChange}
              placeholder={
                isEmptyReport && !hasErrors
                  ? t("placeholder-explain-empty-report")
                  : t("placeholder-explain-changes")
              }
              rows={4}
              maxLength={maxLength}
            />
            <CharacterCount>
              {changeString.length} / {maxLength}
            </CharacterCount>
          </ExplanationSection>
        </ErrorsSection>
      )}
    </Container>
  );
};

export default ChangeErrorDisplay;
